import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { FigureCard, SectionHeading } from './components/BrandUI';
import './App.css';

const heroSlides = [
  {
    kicker: 'Enter the portal',
    title: 'Mystery Summon',
    copy: 'Pull from themed anime banners and discover which figure answers your call.',
    cta: 'Summon Now',
    to: '/summon',
    image: '/images/banners/all-banner.jpg',
    accent: '限定召喚',
  },
  {
    kicker: 'Fresh from Japan',
    title: 'Latest Arrivals',
    copy: 'Explore the newest heroes, rivals, and legends added to the NFig collection.',
    cta: 'Explore Figures',
    to: '/browse',
    image: '/images/banners/one-piece-banner.jpg',
    accent: '新着',
  },
  {
    kicker: 'Build your shelf',
    title: 'Find Your Series',
    copy: 'Search across iconic worlds and bring your favorite anime moments home.',
    cta: 'Browse Collection',
    to: '/browse',
    image: '/images/banners/db-op-banner.jpg',
    accent: 'コレクション',
  },
];

const featuredSeries = [
  { name: 'Dragon Ball', image: '/images/banners/dragon-ball-banner.jpg' },
  { name: 'One Piece', image: '/images/banners/one-piece-banner.jpg' },
  { name: 'Naruto', image: '/images/banners/naruto-banner.jpg' },
  { name: 'Jujutsu Kaisen', image: '/images/banners/jjk-banner.jpg' },
];

export default function App({ figurineClient = supabase }) {
  const [latest, setLatest] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const arrivalsRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await figurineClient
        .from('figurines')
        .select('id,name,value,series,rating,stock,image_url,animation_url,image,animation,added_at')
        .gt('stock', 0)
        .order('added_at', { ascending: false })
        .order('id', { ascending: false })
        .limit(8);

      if (!error) setLatest(data ?? []);
      else console.error('Failed to fetch latest figurines:', error);
      setLoading(false);
    })();
  }, [figurineClient]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6500);

    return () => window.clearInterval(interval);
  }, []);

  const moveSlide = (direction) => {
    setActiveSlide((current) => (current + direction + heroSlides.length) % heroSlides.length);
  };

  const scrollArrivals = (direction) => {
    arrivalsRef.current?.scrollBy({ left: direction * 280, behavior: 'smooth' });
  };

  return (
    <div className="nfig-page pb-20">
      <main className="nfig-container space-y-20">
        <section className="relative overflow-hidden rounded-2xl border border-blue-400/30 bg-[#101727] shadow-[0_28px_90px_rgba(0,0,0,0.5)]" aria-label="Featured promotions">
          <div className="relative min-h-[520px] sm:min-h-[570px] lg:min-h-[620px]">
            {heroSlides.map((slide, index) => (
              <article
                key={slide.title}
                className={`absolute inset-0 transition-all duration-700 ${
                  index === activeSlide ? 'z-10 opacity-100' : 'pointer-events-none opacity-0'
                }`}
                aria-hidden={index !== activeSlide}
              >
                <img src={slide.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#070b15] via-[#0b1020]/90 to-[#0b1020]/30" />
                <div className="absolute inset-0 opacity-20 [background-image:repeating-linear-gradient(120deg,transparent_0_38px,rgba(185,217,255,0.35)_39px,transparent_40px_70px)]" />
                <div className="relative z-10 flex min-h-[520px] max-w-3xl flex-col justify-center px-7 py-20 sm:min-h-[570px] sm:px-12 lg:min-h-[620px] lg:px-20">
                  <div className="mb-5 flex items-center gap-3">
                    <span className="h-px w-12 bg-[#1683f3] shadow-[0_0_12px_#1683f3]" />
                    <p className="nfig-kicker">{slide.kicker}</p>
                  </div>
                  <p className="mb-2 text-sm font-bold tracking-[0.4em] text-blue-300/70">{slide.accent}</p>
                  <h1 className="max-w-2xl text-5xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-6xl lg:text-7xl">
                    {slide.title}
                  </h1>
                  <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">{slide.copy}</p>
                  <div className="mt-8">
                    <Link to={slide.to} className="nfig-button-primary">{slide.cta} <span aria-hidden="true">→</span></Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="absolute bottom-5 left-5 right-5 z-20 flex items-center justify-between sm:bottom-8 sm:left-8 sm:right-8">
            <div className="flex gap-2">
              {heroSlides.map((slide, index) => (
                <button
                  key={slide.title}
                  type="button"
                  onClick={() => setActiveSlide(index)}
                  className={`h-1.5 rounded-full transition-all ${index === activeSlide ? 'w-10 bg-[#1683f3] shadow-[0_0_10px_#1683f3]' : 'w-5 bg-white/30 hover:bg-white/60'}`}
                  aria-label={`Show slide ${index + 1}: ${slide.title}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => moveSlide(-1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/30 text-xl backdrop-blur transition hover:border-blue-300 hover:bg-blue-500/20" aria-label="Previous slide">←</button>
              <button type="button" onClick={() => moveSlide(1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-black/30 text-xl backdrop-blur transition hover:border-blue-300 hover:bg-blue-500/20" aria-label="Next slide">→</button>
            </div>
          </div>
        </section>

        <section>
          <SectionHeading
            eyebrow="Recently unboxed"
            title="New Arrivals"
            description="The latest in-stock figures to join the NFig lineup."
            action={(
              <div className="flex items-center gap-2">
                <button onClick={() => scrollArrivals(-1)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-blue-400/20 bg-white/5 hover:border-blue-400/60" aria-label="Scroll arrivals left">←</button>
                <button onClick={() => scrollArrivals(1)} className="flex h-10 w-10 items-center justify-center rounded-lg border border-blue-400/20 bg-white/5 hover:border-blue-400/60" aria-label="Scroll arrivals right">→</button>
                <Link to="/browse" className="ml-2 text-xs font-black uppercase tracking-widest text-blue-300 hover:text-white">See all</Link>
              </div>
            )}
          />

          {loading ? (
            <div className="nfig-panel rounded-xl p-8 text-slate-400">Loading the latest figures…</div>
          ) : latest.length === 0 ? (
            <div className="nfig-panel rounded-xl p-8 text-slate-400">No items yet.</div>
          ) : (
            <div ref={arrivalsRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-5 [scrollbar-color:#1683f3_#171d2b]">
              {latest.map((figure) => (
                <div key={figure.id} className="snap-start">
                  <FigureCard figure={figure} compact />
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionHeading eyebrow="Choose your world" title="Shop by Series" description="Start with a universe you love and discover its strongest shelf presence." />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {featuredSeries.map((series, index) => (
              <Link
                key={series.name}
                to="/browse"
                className="group relative min-h-64 overflow-hidden rounded-xl border border-blue-400/20 bg-[#171d2b] transition hover:-translate-y-1 hover:border-blue-400/70 hover:shadow-[0_16px_45px_rgba(22,131,243,0.15)]"
              >
                <img src={series.image} alt="" className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080c18] via-[#0b1020]/50 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <span className="text-[0.6rem] font-black uppercase tracking-[0.3em] text-blue-300">Series 0{index + 1}</span>
                  <h3 className="mt-1 text-xl font-black uppercase text-white">{series.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="nfig-panel relative overflow-hidden rounded-2xl">
          <img src="/images/banners/all-banner.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b1020] via-[#0b1020]/95 to-[#0b1020]/45" />
          <div className="relative z-10 grid min-h-80 items-center gap-8 p-8 sm:p-12 lg:grid-cols-[1fr_auto] lg:p-16">
            <div>
              <p className="nfig-kicker">召喚スポットライト / Summon spotlight</p>
              <h2 className="mt-3 text-3xl font-black uppercase text-white sm:text-5xl">Let fate choose the figure</h2>
              <p className="mt-4 max-w-2xl text-slate-300">Select a themed banner, watch the reveal, and add a little gacha energy to your collection.</p>
            </div>
            <Link to="/summon" className="nfig-button-primary whitespace-nowrap">Enter Summon</Link>
          </div>
        </section>
      </main>
    </div>
  );
}
